import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { canAccess } from '@evogard/domain';
import type { Context } from '@evogard/contract';
import { api, ok } from './api.js';

export const CONTEXT_KEY = ['plataforma', 'contexto'] as const;

/**
 * O contexto da sessão: quem é, em que base está, e o que pode.
 *
 * `null` quer dizer "não autenticado" — não é erro, é estado. Erro de rede é
 * outra coisa e aparece como erro.
 */
export const contextOptions = queryOptions({
  queryKey: CONTEXT_KEY,
  queryFn: async (): Promise<Context | null> => {
    const r = await api.platform.context();
    if (r.status === 200) return r.body;
    if (r.status === 401) return null;
    throw new Error('Não foi possível carregar a sessão.');
  },
  retry: false,
  staleTime: 30_000,
});

export function useSessionContext() {
  return useQuery(contextOptions);
}

export function useSwitchTenant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: number) => {
      const r = await api.platform.switchTenant({ body: { tenantId } });
      return ok(r, 200, 'Não foi possível trocar de base.');
    },
    onSuccess: (context) => {
      client.setQueryData(CONTEXT_KEY, context);
      // as permissões mudam com a base: tudo que foi carregado antes vale menos
      void client.invalidateQueries();
    },
  });
}

/**
 * Grava preferência, e atualiza o contexto com o que o servidor devolveu.
 *
 * Mora aqui, e não dentro da tela de preferências, porque **dois lugares
 * gravam**: a tela e o seletor de tema do menu de conta. Com uma cópia em cada
 * um, bastaria alguém consertar o tratamento de erro num deles para os dois
 * passarem a se comportar diferente — e o do menu é justamente o que ninguém
 * abre para revisar.
 *
 * `setQueryData` e não `invalidate`: o servidor acabou de dizer o que valeu, e
 * buscar de novo é perguntar o que já foi respondido. Diferente de
 * `useSwitchTenant`, que invalida tudo — lá a base mudou e o que estava
 * carregado é de outra praça; aqui só mudou uma preferência da pessoa.
 */
export function useSavePreferences() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (change: Partial<Context['preferences']>) => {
      const r = await api.platform.writePreferences({ body: change });
      return ok(r, 200, 'Não foi possível salvar. Tente de novo.');
    },
    onSuccess: (fresh) => {
      client.setQueryData(CONTEXT_KEY, (old: Context | null | undefined) =>
        old ? { ...old, preferences: fresh } : old,
      );
    },
  });
}

/** Espelha exatamente a regra do backend — e vem do mesmo pacote. */
export function can(
  context: Context | null | undefined,
  resource: string,
  action: string,
) {
  if (!context) return false;
  return canAccess(context.permissions, resource, action);
}
